import { Link, type LinkProps } from "react-router-dom";
import { forwardRef, useCallback, useRef } from "react";
import { prefetchForHref } from "@/lib/routePrefetch";

/**
 * Drop-in replacement for react-router-dom Link that prefetches
 * the route's lazy-loaded chunk on hover/touchstart/focus.
 * Eliminates perceived navigation "lag" by downloading the chunk
 * the moment the user shows intent.
 */
type Props = LinkProps & {
  prefetch?: () => Promise<unknown>;
};

const idle = (cb: () => void) => {
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(cb, { timeout: 500 });
  } else {
    setTimeout(cb, 0);
  }
};

const PrefetchLink = forwardRef<HTMLAnchorElement, Props>(
  ({ prefetch, to, onMouseEnter, onTouchStart, onFocus, ...rest }, ref) => {
    const fired = useRef(false);

    const trigger = useCallback(() => {
      if (fired.current) return;
      fired.current = true;
      const fn = prefetch ?? (typeof to === "string" ? prefetchForHref(to) : undefined);
      if (!fn) return;
      idle(() => { try { fn(); } catch { /* swallow */ } });
    }, [prefetch, to]);

    return (
      <Link
        {...rest}
        ref={ref}
        to={to}
        onMouseEnter={(e) => { trigger(); onMouseEnter?.(e); }}
        onTouchStart={(e) => { trigger(); onTouchStart?.(e); }}
        onFocus={(e) => { trigger(); onFocus?.(e); }}
      />
    );
  }
);

PrefetchLink.displayName = "PrefetchLink";

export default PrefetchLink;
